import React, { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardBody, Col, Container, Nav, NavItem, Row } from "reactstrap";
import classnames from "classnames";
import BreadCrumb from "components/common/BreadCrumb";
import CompanyTab from "./CompanyTab";
import BankTab from "./BankTab";
import SmtpTab from "./SmtpTab";
// import AppSettingsTab from "./AppSettingsTab";
import ChangePasswordTab from "./ChangePasswordTab";
// import LeavePolicyTab from "./LeavePolicyTab";
import EmployeeProfileTab from "./EmployeeProfileTab";
import { getDocumentTitle } from "utils/helpers";
import { useProfile } from "features/Authentication/hooks/useProfile";

const SUPER_ADMIN_TABS = [
  {
    id: "company",
    label: "Company",
    description: "Legal name, contact, and GST for your organization",
    icon: "ri-building-4-line",
    component: CompanyTab,
  },
  {
    id: "bank",
    label: "Bank details",
    description: "Account used for invoices and payouts",
    icon: "ri-bank-line",
    component: BankTab,
  },
  {
    id: "smtp",
    label: "SMTP",
    description: "Outgoing email server (requires company profile)",
    icon: "ri-mail-settings-line",
    component: SmtpTab,
  },
  // App settings (JSON) — hidden for now; restore import + tab when needed.
  // {
  //   id: "app",
  //   label: "App settings (JSON)",
  //   description: "Shallow-merge key/value store for integrations and feature flags",
  //   icon: "ri-code-s-slash-line",
  //   component: AppSettingsTab,
  // },
  // {
  //   id: "leave-policy",
  //   label: "Leave policies",
  //   description: "Accrual rules per employment category and leave type",
  //   icon: "ri-calendar-check-line",
  //   component: LeavePolicyTab,
  // },
];

const BASE_TABS = [
  {
    id: "password",
    label: "Change password",
    description: "Update your account password",
    icon: "ri-lock-password-line",
    component: ChangePasswordTab,
  },
];

const EMPLOYEE_PROFILE_TAB = {
  id: "employee-profile",
  label: "Employee profile",
  description: "Your personal address and contact details",
  icon: "ri-user-settings-line",
  component: EmployeeProfileTab,
};

const isValidTab = (tabs: Array<{ id: string }>, tab: string | null): boolean =>
  tab !== null && tabs.some((t) => t.id === tab);

const Settings = () => {
  document.title = getDocumentTitle("System Settings");
  const { isEmployee, isSuperAdmin } = useProfile();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");

  const visibleTabs = useMemo(() => {
    const tabs = isSuperAdmin
      ? [...SUPER_ADMIN_TABS, ...BASE_TABS]
      : [...BASE_TABS];
    return isEmployee ? [EMPLOYEE_PROFILE_TAB, ...tabs] : tabs;
  }, [isEmployee, isSuperAdmin]);

  const activeTab = isValidTab(visibleTabs, tabParam)
    ? tabParam!
    : visibleTabs[0].id;

  const activeConfig = useMemo(
    () => visibleTabs.find((t) => t.id === activeTab) ?? visibleTabs[0],
    [activeTab, visibleTabs],
  );
  const ActiveComponent = activeConfig.component;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="System Settings" pageTitle="Administration" />
          <Row className="position-relative">
            <Col xxl={3} lg={4} className="mb-4 mb-lg-0">
              <div className="position-sticky" style={{ top: "6rem" }}>
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-0">
                    <div className="p-4 border-bottom bg-light bg-opacity-50">
                      <h5 className="mb-1 fw-semibold">Settings</h5>
                      <p className="text-muted small mb-0">
                        Company, bank, SMTP, leave policies, and your password
                      </p>
                    </div>
                    <Nav
                      className="nav flex-column nav-pills nav-pills-custom p-3"
                      role="tablist"
                    >
                      {visibleTabs.map((tab) => {
                        const isActive = activeTab === tab.id;
                        return (
                          <NavItem key={tab.id} className="mb-1">
                            <Link
                              to={`?tab=${tab.id}`}
                              className={classnames(
                                "nav-link py-3 px-3 rounded d-flex align-items-center text-decoration-none",
                                {
                                  "active bg-primary text-white": isActive,
                                  "text-body": !isActive,
                                },
                              )}
                              role="tab"
                              aria-selected={isActive}
                            >
                              <i
                                className={classnames(
                                  tab.icon,
                                  "me-3 fs-5",
                                  isActive ? "text-white" : "text-primary",
                                )}
                              />
                              <span className="fw-medium">{tab.label}</span>
                            </Link>
                          </NavItem>
                        );
                      })}
                    </Nav>
                  </CardBody>
                </Card>
              </div>
            </Col>

            <Col xxl={9} lg={8}>
              <Card className="border-0 shadow-sm">
                <CardBody className="p-4">
                  <div className="mb-4">
                    <h4 className="mb-1">{activeConfig.label}</h4>
                    <p className="text-muted mb-0">
                      {activeConfig.description}
                    </p>
                  </div>
                  <ActiveComponent />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Settings;
